import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { DuplicatesRepairPanel } from '../components/DuplicatesRepairPanel';
import { TreasuryRenumberPanel } from '../components/TreasuryRenumberPanel';
import { ArticleSanitationPanel } from '../components/ArticleSanitationPanel';
import { MirrorArchivePanel } from '../components/MirrorArchivePanel';
import { LegacyInvoiceImportPanel } from '../components/LegacyInvoiceImportPanel';
import { LegacyBulkImportPanel } from '../components/LegacyBulkImportPanel';
import { Activity, AlertTriangle, BookOpen, CheckCircle2, Clock, Database, FileSpreadsheet, HardDrive, PlayCircle, RefreshCw, ShieldCheck, Wrench, Trash2, X, Users, LogOut, Monitor, Download, Upload, RotateCcw, Cloud, CloudUpload, Unplug, Construction, SlidersHorizontal, Package } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { fmtDateTime } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLES = {
  safe: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-amber-100 text-amber-800',
  high: 'bg-rose-100 text-rose-800',
};

function MaintenanceCenter() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [checks, setChecks] = useState([]);
  const [checkResults, setCheckResults] = useState({}); // {check_id: {count, samples, preview_label}}
  const [scheduled, setScheduled] = useState([]);
  const [jobsLog, setJobsLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmRepair, setConfirmRepair] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [runningCheckId, setRunningCheckId] = useState(null);
  const [mig, setMig] = useState(null);
  const [migBusy, setMigBusy] = useState(false);
  const [revMig, setRevMig] = useState(null);
  const [revMigBusy, setRevMigBusy] = useState(false);

  const loadReversalMigration = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/migrations/journal-reversals`, { withCredentials: true });
      setRevMig(r.data);
    } catch { setRevMig({ pending: 0 }); }
  };

  const applyReversalMigration = async () => {
    setRevMigBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/accounting/migrations/journal-reversals/apply`, {}, { withCredentials: true });
      toast.success(`Migración aplicada: ${r.data.entries_to_reverse} asiento(s) reversado(s)`);
      loadReversalMigration();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al aplicar la migración'); }
    finally { setRevMigBusy(false); }
  };

  const [links, setLinks] = useState(null);
  const [linksBusy, setLinksBusy] = useState(false);

  const loadLinks = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/billing/migrations/scheme-links`, { withCredentials: true });
      setLinks(r.data);
    } catch { setLinks(null); }
  };
  const applyLinks = async () => {
    setLinksBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/billing/migrations/scheme-links/apply`, {}, { withCredentials: true });
      const sc = r.data.scheme || {};
      toast.success(`Vinculación aplicada: ${r.data.residents_linked} convenios · ${r.data.payers_set} pagadores`
        + ((sc.services_updated || sc.insurers_updated) ? ` · catálogo normalizado (${sc.services_updated} conceptos, ${sc.insurers_updated} convenios)` : ''));
      loadLinks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al aplicar'); }
    finally { setLinksBusy(false); }
  };

  const [act, setAct] = useState(null);
  const [actBusy, setActBusy] = useState(false);

  // v3.36.8 · Limpieza de supplier_cc zombies
  const [ccZombies, setCcZombies] = useState(null);
  const [ccZombiesBusy, setCcZombiesBusy] = useState(false);
  const loadCcZombies = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/supplier-cc-zombies`, { withCredentials: true });
      setCcZombies(r.data);
    } catch { setCcZombies(null); }
  };
  const applyCcZombies = async () => {
    if (!window.confirm(`Vas a eliminar ${ccZombies?.count} movimiento(s) huérfanos de la CC de proveedores por $ ${(ccZombies?.total_amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}. ¿Continúo?`)) return;
    setCcZombiesBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/maintenance/supplier-cc-zombies/apply`, { confirm: 'LIMPIAR' }, { withCredentials: true });
      toast.success(`Limpieza completada: ${r.data.deleted} movimiento(s) eliminados`);
      loadCcZombies();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al limpiar'); }
    finally { setCcZombiesBusy(false); }
  };

  // v3.45.2 · Fix artículos con unit=null
  const [unitNullFix, setUnitNullFix] = useState(null);
  const [unitNullBusy, setUnitNullBusy] = useState(false);
  const loadUnitNullFix = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/fix-articles-unit-null/preview`, { withCredentials: true });
      setUnitNullFix(r.data);
    } catch { setUnitNullFix(null); }
  };
  const applyUnitNullFix = async () => {
    if (!window.confirm(`Vas a setear unit="unidad" en ${unitNullFix?.articles_with_null_unit} artículo(s) con campo vacío/nulo. Operación idempotente y segura. ¿Continúo?`)) return;
    setUnitNullBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/maintenance/fix-articles-unit-null/apply`, {}, { withCredentials: true });
      toast.success(`v3.45.2 · ${r.data.articles_fixed} artículo(s) corregidos · unit=null → "unidad"`);
      loadUnitNullFix();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al aplicar el fix'); }
    finally { setUnitNullBusy(false); }
  };

  // v3.46.0 · Consistencia de stock (articles.stock == Σ lots)
  const [stockConsistency, setStockConsistency] = useState(null);  // null=loading, object=loaded
  const [stockConsistencyChecking, setStockConsistencyChecking] = useState(false);
  const [stockConsistencyFixing, setStockConsistencyFixing] = useState(false);
  const loadStockConsistency = async () => {
    setStockConsistencyChecking(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/stock-consistency/check`, { withCredentials: true });
      setStockConsistency(r.data);
    } catch { setStockConsistency(null); }
    finally { setStockConsistencyChecking(false); }
  };
  const applyStockConsistency = async () => {
    if (!window.confirm(
      `Vas a reconciliar articles.stock = Σ(lotes) para ${stockConsistency?.total_mismatches} artículo(s).\n\n` +
      `Esta operación es segura e idempotente: el inventario de lotes es la fuente de verdad (FEFO). ` +
      `Se guarda un snapshot de respaldo antes de modificar.\n\n¿Continuar?`
    )) return;
    setStockConsistencyFixing(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/maintenance/stock-consistency/apply`, {}, { withCredentials: true });
      if (r.data.status === 'already_clean') {
        toast.success('Sin diferencias — el stock ya estaba sincronizado con los lotes.');
      } else {
        toast.success(`v3.46.0 · ${r.data.articles_fixed} artículo(s) reconciliados · Snapshot: ${r.data.run_id}`);
      }
      loadStockConsistency();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al reconciliar stock'); }
    finally { setStockConsistencyFixing(false); }
  };

  // v3.42.0 · Migración de IVA en Rendiciones de Anticipo (desconsolidación)
  const [advIvaMig, setAdvIvaMig] = useState(null);
  const [advIvaMigBusy, setAdvIvaMigBusy] = useState(false);
  const loadAdvIvaMig = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/advance-iva-migration/preview`, { withCredentials: true });
      setAdvIvaMig(r.data);
    } catch { setAdvIvaMig(null); }
  };
  const applyAdvIvaMig = async () => {
    if (!window.confirm(`Vas a migrar ${advIvaMig?.count} rendición(es) de anticipo. Se generará UN contra-asiento del consolidado original + un asiento nuevo por cada comprobante fiscal. IVA total afectado: $ ${(advIvaMig?.iva_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}. ¿Continúo?`)) return;
    setAdvIvaMigBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/maintenance/advance-iva-migration/apply`, {}, { withCredentials: true });
      toast.success(`Migración v3.42.0 completada: ${r.data.migrated_count} rendición(es) desconsolidada(s)` + (r.data.errors?.length ? ` · ${r.data.errors.length} error(es)` : ''));
      loadAdvIvaMig();
      loadAdvIvaDates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al migrar'); }
    finally { setAdvIvaMigBusy(false); }
  };

  // v3.42.3 · Reparar fechas de contra-asientos de la migración v3.42.0
  const [advIvaDates, setAdvIvaDates] = useState(null);
  const [advIvaDatesBusy, setAdvIvaDatesBusy] = useState(false);
  const loadAdvIvaDates = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/advance-iva-migration/repair-reversal-dates`, { withCredentials: true });
      setAdvIvaDates(r.data);
    } catch { setAdvIvaDates(null); }
  };
  const applyAdvIvaDates = async () => {
    if (!window.confirm(`Vas a realinear la fecha de ${advIvaDates?.count} contra-asiento(s) de la migración IVA v3.42.0 para que coincidan con la fecha de su asiento original. Esto limpia la Posición de IVA mensual. ¿Continúo?`)) return;
    setAdvIvaDatesBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/maintenance/advance-iva-migration/repair-reversal-dates/apply`, {}, { withCredentials: true });
      toast.success(`Reparación v3.42.3 completada: ${r.data.repaired_count} contra-asiento(s) realineado(s).`);
      loadAdvIvaDates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al reparar'); }
    finally { setAdvIvaDatesBusy(false); }
  };

  // v3.28.7 · Normalización de adicionales 400 / 801
  const [normAdic, setNormAdic] = useState(null);
  const [normAdicBusy, setNormAdicBusy] = useState(false);

  const loadNormAdic = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/billing/migrations/normalize-adicionales`, { withCredentials: true });
      setNormAdic(r.data);
    } catch { setNormAdic(null); }
  };
  const applyNormAdic = async () => {
    setNormAdicBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/billing/migrations/normalize-adicionales/apply`, {}, { withCredentials: true });
      toast.success(`Normalización aplicada: ${r.data.changes} asignación(es) actualizada(s) · ${r.data.already_ok} ya estaban OK`);
      loadNormAdic();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al normalizar los adicionales'); }
    finally { setNormAdicBusy(false); }
  };

  const loadActivation = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/billing/migrations/activate-prestacional`, { withCredentials: true });
      setAct(r.data);
    } catch { setAct(null); }
  };
  const applyActivation = async () => {
    setActBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/billing/migrations/activate-prestacional/apply`, {}, { withCredentials: true });
      const sc = r.data.scheme || {};
      toast.success(`Conceptos activados: ${r.data.pending} · ya estaban activos: ${r.data.already_active}`
        + ((sc.services_updated || sc.insurers_updated) ? ` · catálogo normalizado (${sc.services_updated} conceptos, ${sc.insurers_updated} convenios)` : ''));
      loadActivation();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al activar los conceptos'); }
    finally { setActBusy(false); }
  };

  const loadMigration = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/billing/migrations/nc-credited`, { withCredentials: true });
      setMig(r.data);
    } catch { setMig({ pending: 0 }); }
  };

  const applyMigration = async () => {
    setMigBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/billing/migrations/nc-credited/apply`, {}, { withCredentials: true });
      toast.success(`Migración aplicada: ${r.data.invoices_to_fix} factura(s) recalculada(s) y ${r.data.letters_to_backfill} letra(s) completada(s)`);
      loadMigration();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al aplicar la migración'); }
    finally { setMigBusy(false); }
  };

  // v3.28.32 · Backfill descripciones "Cobro Residente" en Libro Diario
  const [descBackfill, setDescBackfill] = useState(null);
  const [descBackfillBusy, setDescBackfillBusy] = useState(false);
  const runDescBackfill = async (apply) => {
    setDescBackfillBusy(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/maintenance/backfill/cobro-residente-desc?apply=${apply ? 'true' : 'false'}`,
        {},
        { withCredentials: true }
      );
      setDescBackfill(r.data);
      if (apply) {
        toast.success(`Backfill aplicado: ${r.data.updated} asiento(s) actualizado(s) de ${r.data.total_entries} totales`);
      } else {
        toast.info(`Vista previa: ${r.data.to_update} asiento(s) por actualizar sobre ${r.data.total_entries} totales`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al ejecutar el backfill de descripciones'); }
    finally { setDescBackfillBusy(false); }
  };
  const [runningJobId, setRunningJobId] = useState(null);
  const [fullBackupExporting, setFullBackupExporting] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);
  // Backup ZIP + Restauración
  const [zipBackupExporting, setZipBackupExporting] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  // Google Drive backup (off-site)
  const [tab, setTab] = useState('health');
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveBusy, setDriveBusy] = useState(false);
  // Modo Mantenimiento
  const [maintMode, setMaintMode] = useState(null);
  const [maintMsg, setMaintMsg] = useState('');
  const [maintReason, setMaintReason] = useState('');
  const [maintUntil, setMaintUntil] = useState('');
  const [maintBusy, setMaintBusy] = useState(false);

  // v3.30.11.1 · Diagnóstico Fase 2A: espejos históricos y nuevos
  const [mirrorStats, setMirrorStats] = useState(null);
  const [mirrorBusy, setMirrorBusy] = useState(false);
  const loadMirrorStats = useCallback(async () => {
    setMirrorBusy(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/mirror-stats`, { withCredentials: true });
      setMirrorStats(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo leer el estado de espejos');
    } finally { setMirrorBusy(false); }
  }, []);
  useEffect(() => { if (user?.role === 'admin') loadMirrorStats(); }, [user, loadMirrorStats]);

  const loadBackupStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/backup-status`, { withCredentials: true });
      setBackupStatus(r.data);
    } catch { setBackupStatus(null); }
  }, []);

  useEffect(() => { if (user?.role === 'admin') loadBackupStatus(); }, [user, loadBackupStatus]);

  // ===== PARÁMETROS OPERATIVOS =====
  const [tolInput, setTolInput] = useState('0');
  const [paramsBusy, setParamsBusy] = useState(false);
  useEffect(() => {
    if (user?.role !== 'admin') return;
    axios.get(`${BACKEND_URL}/api/maintenance/params`, { withCredentials: true })
      .then(r => setTolInput(String(r.data.reception_tolerance_pct ?? 0)))
      .catch(() => {});
  }, [user]);
  const saveParams = async () => {
    const v = parseFloat(tolInput);
    if (isNaN(v) || v < 0 || v > 50) { toast.error('La tolerancia debe ser un porcentaje entre 0 y 50'); return; }
    setParamsBusy(true);
    try {
      await axios.put(`${BACKEND_URL}/api/maintenance/params`, { reception_tolerance_pct: v }, { withCredentials: true });
      toast.success('Parámetros guardados');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally { setParamsBusy(false); }
  };

  // ===== GOOGLE DRIVE (backup off-site) =====
  const fetchDriveStatus = useCallback(async () => {
    if (user?.role !== 'admin') return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/drive/status`, { withCredentials: true });
      setDriveStatus(r.data);
    } catch { /* silencioso */ }
  }, [user]);

  useEffect(() => {
    fetchDriveStatus();
    const params = new URLSearchParams(window.location.search);
    const d = params.get('drive');
    if (d === 'connected') { toast.success('Google Drive conectado correctamente'); setTab('scheduled'); }
    else if (d === 'error') { toast.error('No se pudo conectar Google Drive' + (params.get('reason') ? `: ${params.get('reason')}` : '')); setTab('scheduled'); }
    if (d) { window.history.replaceState({}, '', window.location.pathname); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDriveStatus]);

  const handleConnectDrive = async () => {
    setDriveBusy(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/drive/auth`, { withCredentials: true });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar la conexión con Google Drive');
      setDriveBusy(false);
    }
  };

  const handleDisconnectDrive = async () => {
    if (!window.confirm('¿Desvincular Google Drive? Los backups diarios dejarán de subirse a la nube.')) return;
    setDriveBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/drive/disconnect`, {}, { withCredentials: true });
      toast.success('Google Drive desvinculado');
      fetchDriveStatus();
    } catch { toast.error('Error al desvincular'); } finally { setDriveBusy(false); }
  };

  const handleTestDriveBackup = async () => {
    setDriveBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/drive/test-backup`, {}, { withCredentials: true });
      toast.success(`Backup subido a Google Drive: ${r.data.file}`);
      fetchDriveStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al subir el backup'); } finally { setDriveBusy(false); }
  };

  // ===== MODO MANTENIMIENTO =====
  const fetchMaintMode = useCallback(async () => {
    if (user?.role !== 'admin') return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance-mode`, { withCredentials: true });
      setMaintMode(r.data);
      setMaintMsg(r.data.message || '');
      setMaintReason(r.data.reason || '');
      setMaintUntil(r.data.until || '');
    } catch { /* silencioso */ }
  }, [user]);

  useEffect(() => { fetchMaintMode(); }, [fetchMaintMode]);

  const toggleMaintMode = async (activate) => {
    setMaintBusy(true);
    try {
      await axios.put(`${BACKEND_URL}/api/maintenance-mode`, {
        active: activate, message: maintMsg, reason: maintReason, until: maintUntil,
      }, { withCredentials: true });
      toast.success(activate ? 'Modo Mantenimiento ACTIVADO' : 'Modo Mantenimiento desactivado');
      fetchMaintMode();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar el Modo Mantenimiento');
    } finally { setMaintBusy(false); }
  };

  const handleCloseSessions = async () => {
    if (!window.confirm('¿Cerrar las sesiones abiertas de todos los usuarios que NO son administradores? Tendrán que volver a iniciar sesión. (No te afecta a vos ni a otros admins.)')) return;
    setMaintBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/maintenance-mode/close-sessions`, {}, { withCredentials: true });
      toast.success(`Se cerraron ${r.data.closed} sesión(es) de usuarios no-admin`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cerrar las sesiones');
    } finally { setMaintBusy(false); }
  };

  const onZipBackup = async () => {
    setZipBackupExporting(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/full-backup`, { withCredentials: true, responseType: 'blob' });
      const dispo = r.headers['content-disposition'] || '';
      const m = dispo.match(/filename=([^;]+)/);
      const fname = m ? m[1].trim() : `backup_completo_${Date.now()}.zip`;
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = fname; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Backup descargado: ${fname}`);
      loadBackupStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo generar el backup completo');
    } finally { setZipBackupExporting(false); }
  };

  const handleRestoreBackup = async () => {
    if (!restoreFile) { toast.error('Elegí primero el archivo .zip del backup.'); return; }
    if (restoreConfirm.trim() !== 'RESTAURAR') { toast.error('Escribí RESTAURAR para confirmar.'); return; }
    setRestoring(true); setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append('file', restoreFile);
      fd.append('confirm', 'RESTAURAR');
      const r = await axios.post(`${BACKEND_URL}/api/maintenance/full-restore`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      setRestoreResult(r.data);
      toast.success(`Backup restaurado: ${r.data.collections} colecciones, ${r.data.total_docs} documentos.`);
      setRestoreFile(null); setRestoreConfirm('');
      loadBackupStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo restaurar el backup.');
    } finally { setRestoring(false); }
  };

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/active-sessions`, { withCredentials: true });
      setSessions(r.data.sessions || []);
    } catch { toast.error('Error al cargar sesiones'); } finally { setSessionsLoading(false); }
  }, []);

  useEffect(() => { if (user?.role === 'admin') fetchSessions(); }, [user, fetchSessions]);

  const revokeSession = async () => {
    if (!confirmRevoke) return;
    setRevoking(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/active-sessions/${confirmRevoke.session_id}/revoke`, {}, { withCredentials: true });
      toast.success('Sesión cerrada');
      setConfirmRevoke(null);
      fetchSessions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cerrar sesión'); } finally { setRevoking(false); }
  };

  const deviceLabel = (ua) => {
    if (!ua || ua === '—') return 'Desconocido';
    const s = ua.toLowerCase();
    let os = 'Equipo';
    if (s.includes('android')) os = 'Android';
    else if (s.includes('iphone') || s.includes('ipad') || s.includes('ios')) os = 'iOS';
    else if (s.includes('windows')) os = 'Windows';
    else if (s.includes('mac os') || s.includes('macintosh')) os = 'Mac';
    else if (s.includes('linux')) os = 'Linux';
    else if (s.includes('curl')) os = 'API/curl';
    let br = '';
    if (s.includes('edg/')) br = 'Edge';
    else if (s.includes('chrome')) br = 'Chrome';
    else if (s.includes('firefox')) br = 'Firefox';
    else if (s.includes('safari')) br = 'Safari';
    return br ? `${os} · ${br}` : os;
  };

  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Solo administradores');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, c, sj, sjl] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/maintenance/health-summary`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/maintenance/system-alerts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/maintenance/checks`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/maintenance/scheduled-jobs`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/maintenance/scheduled-jobs/log`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setAlerts(a.data || []);
      setChecks(c.data || []);
      setScheduled(sj.data || []);
      setJobsLog(sjl.data || []);
    } catch (e) {
      toast.error('Error al cargar datos de mantenimiento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); loadMigration(); loadReversalMigration(); loadLinks(); loadActivation(); loadNormAdic(); loadCcZombies(); loadAdvIvaMig(); loadAdvIvaDates(); loadUnitNullFix(); loadStockConsistency(); }, [fetchAll]);  // eslint-disable-line react-hooks/exhaustive-deps

  const runCheck = async (cid) => {
    setRunningCheckId(cid);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/maintenance/check/${cid}`, {}, { withCredentials: true });
      setCheckResults((prev) => ({ ...prev, [cid]: r.data }));
    } catch (e) {
      toast.error('Error al verificar');
    } finally {
      setRunningCheckId(null);
    }
  };

  const runRepair = async () => {
    if (!confirmRepair) return;
    setRepairing(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/maintenance/repair/${confirmRepair.id}`, {}, { withCredentials: true });
      toast.success(`Reparado: ${JSON.stringify(r.data)}`);
      setConfirmRepair(null);
      setCheckResults((prev) => ({ ...prev, [confirmRepair.id]: null }));
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reparar');
    } finally {
      setRepairing(false);
    }
  };

  const runJobNow = async (jobId) => {
    setRunningJobId(jobId);
    try {
      await axios.post(`${BACKEND_URL}/api/maintenance/scheduled-jobs/${jobId}/run-now`, {}, { withCredentials: true });
      toast.success(`Job ${jobId} ejecutado`);
      fetchAll();
    } catch (e) {
      toast.error('Error al ejecutar job');
    } finally {
      setRunningJobId(null);
    }
  };

  const dismissAlert = async (kind) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/maintenance/system-alerts/${kind}`, { withCredentials: true });
      setAlerts((prev) => prev.filter((a) => a.kind !== kind));
    } catch (e) {
      toast.error('Error');
    }
  };

  const onFullBackup = async () => {
    setFullBackupExporting(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/all`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `backup_hogar_gestion_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      toast.success('Backup completo descargado');
    } catch (error) {
      let msg = 'Error al exportar datos';
      try {
        if (error.response?.data instanceof Blob) {
          const txt = await error.response.data.text();
          const parsed = JSON.parse(txt);
          msg = parsed.detail || msg;
        } else if (error.response?.data?.detail) {
          msg = error.response.data.detail;
        }
      } catch {}
      toast.error(msg);
    } finally {
      setFullBackupExporting(false);
    }
  };

  if (loading) {
    return <div className="p-8">Cargando…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Wrench className="w-7 h-7 text-cyan-600" /> Centro de Mantenimiento
        </h1>
        <Button variant="outline" onClick={fetchAll} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
        </Button>
      </div>

      {alerts.length > 0 && (
        <Card className="border-rose-300 bg-rose-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Alertas del sistema ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.map((a) => (
              <div key={a.kind} className="flex items-center justify-between bg-white rounded p-3 border border-rose-200">
                <div>
                  <div className="font-semibold">{a.title}</div>
                  <div className="text-xs text-muted-foreground">{a.kind} · {a.created_at && fmtDateTime(a.created_at)}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => dismissAlert(a.kind)} data-testid={`dismiss-${a.kind}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="health" data-testid="tab-health"><Activity className="w-4 h-4 mr-1" /> Salud</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions"><Wrench className="w-4 h-4 mr-1" /> Acciones manuales</TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled"><Clock className="w-4 h-4 mr-1" /> Tareas programadas</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions"><Users className="w-4 h-4 mr-1" /> Sesiones activas{sessions.length > 0 && <Badge className="ml-2 bg-blue-500">{sessions.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="params" data-testid="tab-params"><SlidersHorizontal className="w-4 h-4 mr-1" /> Parámetros</TabsTrigger>
          <TabsTrigger value="dup-repair" data-testid="tab-dup-repair"><AlertTriangle className="w-4 h-4 mr-1" /> Duplicados Tesorería</TabsTrigger>
        </TabsList>

        {/* v3.30.6 · La pestaña "Artículos" (homologación REM + fusión) se mudó a
            → Menú principal → Artículos (Catálogo) → tab "Homologación & Fusión".
            Se removió de acá para no duplicar entradas. */}

        {/* ============== SANEAMIENTO DE DUPLICADOS DE TESORERÍA ============== */}
        <TabsContent value="dup-repair" className="space-y-4">
          <TreasuryRenumberPanel />
        </TabsContent>

        {/* ====================== PARÁMETROS ====================== */}
        <TabsContent value="params" className="space-y-4">
          <Card data-testid="params-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-cyan-600" /> Parámetros de Recepciones
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                <strong>Tolerancia de desvío (%)</strong>: margen aceptado entre la cantidad autorizada en la OC y la recibida para que
                el sistema clasifique la recepción como <strong>Completa</strong>. Fuera de tolerancia: un <strong>faltante</strong> la
                clasifica <strong>Parcial</strong> (el stock recibido impacta igual y la OC cierra como <em>"Recibida c/Faltante"</em>) y un{' '}
                <strong>exceso</strong> la clasifica <strong>Condicionada</strong> (queda retenida hasta revisión de un supervisor).
                Con <strong>0%</strong> la clasificación es estricta.
              </p>
              <div className="flex items-end gap-3">
                <div>
                  <Label htmlFor="tol-input">Tolerancia de desvío (%)</Label>
                  <Input
                    id="tol-input" type="number" min="0" max="50" step="0.5"
                    value={tolInput} onChange={e => setTolInput(e.target.value)}
                    className="w-32" data-testid="param-tolerance-input"
                  />
                </div>
                <Button onClick={saveParams} disabled={paramsBusy} data-testid="save-params-btn">
                  {paramsBusy ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Ejemplo con 5%: pedidos 100 y recibidos 97 → Completa · recibidos 90 → Parcial · recibidos 108 → Condicionada.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====================== SALUD ====================== */}
        <TabsContent value="health" className="space-y-4">
          {/* MODO MANTENIMIENTO */}
          <Card className={maintMode?.active ? 'border-amber-400 bg-amber-50/70 dark:bg-amber-950/20' : ''} data-testid="maintenance-mode-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Construction className="w-4 h-4 text-amber-600" /> Modo Mantenimiento
                {maintMode?.active && <Badge className="bg-amber-500 text-white" data-testid="maint-active-badge">ACTIVO</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <p className="text-muted-foreground">Al activarlo, <strong>todos los usuarios ven un aviso arriba de la pantalla</strong> y los que <strong>no son administradores quedan en solo-lectura</strong> (no pueden cargar ni modificar datos). Vos y los demás admins siguen trabajando normal. Ideal para avisar cuando estás ordenando el catálogo o el stock.</p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label htmlFor="maint-msg">Mensaje para los usuarios</Label>
                  <Input id="maint-msg" value={maintMsg} onChange={e => setMaintMsg(e.target.value)} placeholder="Ej: Estamos ordenando el catálogo y el stock. Por favor no cargues movimientos hasta nuevo aviso." data-testid="maint-message-input" />
                </div>
                <div>
                  <Label htmlFor="maint-reason">Motivo (opcional)</Label>
                  <Input id="maint-reason" value={maintReason} onChange={e => setMaintReason(e.target.value)} placeholder="Ej: Limpieza de catálogo" data-testid="maint-reason-input" />
                </div>
                <div>
                  <Label htmlFor="maint-until">Hora estimada de vuelta (opcional)</Label>
                  <Input id="maint-until" value={maintUntil} onChange={e => setMaintUntil(e.target.value)} placeholder="Ej: 18:00 hs" data-testid="maint-until-input" />
                </div>
              </div>
              {maintMode?.active ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={() => toggleMaintMode(true)} disabled={maintBusy} variant="outline" data-testid="maint-update-btn">Actualizar mensaje</Button>
                  <Button onClick={() => toggleMaintMode(false)} disabled={maintBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="maint-deactivate-btn">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Desactivar mantenimiento
                  </Button>
                  {maintMode.enabled_at && <span className="text-xs text-muted-foreground">Activado por {maintMode.enabled_by} · {fmtDateTime(maintMode.enabled_at)}</span>}
                </div>
              ) : (
                <Button onClick={() => toggleMaintMode(true)} disabled={maintBusy} className="bg-amber-600 hover:bg-amber-700" data-testid="maint-activate-btn">
                  <Construction className="w-4 h-4 mr-2" /> Activar Modo Mantenimiento
                </Button>
              )}
              <div className="pt-3 mt-1 border-t">
                <p className="text-xs text-muted-foreground mb-2">¿Ya hay gente con sesión abierta? Cerrá todas las sesiones de los usuarios <strong>no-admin</strong> para que no sigan trabajando. No te afecta a vos ni a otros administradores.</p>
                <Button variant="outline" onClick={handleCloseSessions} disabled={maintBusy} className="text-red-600 hover:text-red-700 border-red-300" data-testid="close-sessions-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Cerrar sesiones de no-admin
                </Button>
              </div>
            </CardContent>
          </Card>

          {summary && (
            <>
              {/* v3.30.11.1 · Diagnóstico Fase 2A · Espejos históricos */}
              <Card className={mirrorStats?.phase_2a_healthy ? 'border-emerald-300' : 'border-amber-300'} data-testid="mirror-stats-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2">
                      <Package className="w-4 h-4" /> Fase 2A · Artículos espejo (`__d_`)
                      {mirrorStats?.phase_2a_healthy && <Badge className="bg-emerald-500 text-white">Sin nuevos</Badge>}
                      {mirrorStats && !mirrorStats.phase_2a_healthy && <Badge className="bg-amber-500 text-white">Revisar</Badge>}
                    </span>
                    <Button size="sm" variant="outline" onClick={loadMirrorStats} disabled={mirrorBusy} data-testid="mirror-stats-refresh">
                      <RefreshCw className={`w-3 h-3 mr-1 ${mirrorBusy ? 'animate-spin' : ''}`} /> Refrescar
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Fase 2A congeló la creación de nuevos artículos "espejo" (los que tenían sufijo <code>__d_</code>). Los históricos se consolidan con
                    <strong> Acciones manuales → Saneamiento integral de artículos</strong>; las fichas consolidadas quedan vacías (stock 0) solo como historial.
                    El indicador "Sin nuevos" confirma que en las últimas 48 h ninguno se creó.
                  </p>
                  {mirrorStats && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      <div className="border rounded p-2 bg-slate-50 dark:bg-slate-900">
                        <div className="text-[11px] text-muted-foreground">Espejos por consolidar</div>
                        <div className="text-2xl font-bold" data-testid="mirror-total">{mirrorStats.articles_mirrors_pending ?? mirrorStats.articles_mirrors}</div>
                        <div className="text-[10px] text-muted-foreground" data-testid="mirror-historic">Históricos (vacíos): {mirrorStats.articles_mirrors - (mirrorStats.articles_mirrors_pending ?? 0)}</div>
                      </div>
                      <div className={`border rounded p-2 ${mirrorStats.articles_mirrors_last_48h > 0 ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`}>
                        <div className="text-[11px] text-muted-foreground">Nuevos (48 h)</div>
                        <div className={`text-2xl font-bold ${mirrorStats.articles_mirrors_last_48h > 0 ? 'text-amber-800' : 'text-emerald-800'}`} data-testid="mirror-new-48h">{mirrorStats.articles_mirrors_last_48h}</div>
                      </div>
                      <div className="border rounded p-2 bg-slate-50 dark:bg-slate-900">
                        <div className="text-[11px] text-muted-foreground">Lotes con `__d_`</div>
                        <div className="text-2xl font-bold">{mirrorStats.lots_with_suffix}</div>
                        <div className="text-[10px] text-muted-foreground">Nuevos 48h: {mirrorStats.lots_with_suffix_last_48h}</div>
                      </div>
                      <div className="border rounded p-2 bg-slate-50 dark:bg-slate-900">
                        <div className="text-[11px] text-muted-foreground">Movimientos con `__d_`</div>
                        <div className="text-2xl font-bold">{mirrorStats.movements_with_suffix.toLocaleString('es-AR')}</div>
                      </div>
                      <div className="border rounded p-2 bg-slate-50 dark:bg-slate-900">
                        <div className="text-[11px] text-muted-foreground">Última verificación</div>
                        <div className="text-xs font-medium mt-2">{fmtDateTime(mirrorStats.checked_at)}</div>
                      </div>
                    </div>
                  )}
                  {mirrorStats?.top_buckets?.length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Distribución de lotes por depósito destino</summary>
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        {mirrorStats.top_buckets.map(b => (
                          <li key={b.warehouse_id}><strong>{b.warehouse_name || b.warehouse_id}</strong>: {b.lots} lotes</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Movimientos hoy</CardTitle></CardHeader>
                  <CardContent><p className="text-3xl font-bold">{summary.activity?.movements_today ?? 0}</p><p className="text-xs text-muted-foreground">Semana: {summary.activity?.movements_week}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Sesiones</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold">{summary.sessions?.active}</p>
                    <p className="text-xs text-muted-foreground">{summary.sessions?.expired} expiradas para limpiar</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground flex items-center gap-1"><HardDrive className="w-3 h-3" /> Disco</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold">{summary.storage?.disk_pct_used}%</p>
                    <p className="text-xs text-muted-foreground">{summary.storage?.disk_used_gb}GB / {summary.storage?.disk_total_gb}GB</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Usuarios inactivos +90d</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold">{summary.users?.inactive_90d}</p>
                    <p className="text-xs text-muted-foreground">Adjuntos: {summary.attachments?.total} ({summary.attachments?.approx_size_mb}MB)</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Database className="w-4 h-4" /> Colecciones MongoDB</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Colección</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Últimos 7 días</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summary.collections.map((c) => (
                        <TableRow key={c.name} data-testid={`coll-${c.name}`}>
                          <TableCell className="font-mono text-xs">{c.name}</TableCell>
                          <TableCell className="text-right">{c.total.toLocaleString('es-AR')}</TableCell>
                          <TableCell className="text-right text-emerald-700">{c.new_7d > 0 ? `+${c.new_7d}` : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ====================== ACCIONES ====================== */}
        <TabsContent value="actions" className="space-y-3">
          <ArticleSanitationPanel />
          <MirrorArchivePanel />
          <p className="text-sm text-muted-foreground">
            Cada acción primero verifica (modo preview, no toca datos) y muestra lo que encontraría. Recién al apretar "Reparar" se aplica.
          </p>

          {/* v3.42.0 · Desconsolidación de IVA en Rendiciones de Anticipo */}
          <Card className={advIvaMig?.count > 0 ? 'border-amber-300' : ''} data-testid="adv-iva-mig-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Desconsolidar IVA de Rendiciones de Anticipo (v3.42.0)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!advIvaMig ? (
                <p className="text-muted-foreground">Verificando…</p>
              ) : advIvaMig.count === 0 ? (
                <p className="text-muted-foreground" data-testid="adv-iva-mig-done">
                  ✔ No hay rendiciones afectadas: todos los comprobantes fiscales cargados en rendiciones de anticipo ya tienen su asiento contable individual, por lo que la conciliación del Libro IVA Compras los matchea 1:1.
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Antes de v3.42.0, las Rendiciones de Anticipo generaban UN SOLO asiento consolidado por OP (<code>treasury_advance_settlement</code>) que incluía el IVA Crédito Fiscal, pero sin link al <code>purchase_invoice_id</code> individual. Resultado: los asientos de IVA quedaban como <b>huérfanos</b> en la conciliación del Libro IVA Compras (los comprobantes aparecen como "sin asiento" y los asientos como "sin comprobante en el libro").
                    <br/><br/>
                    Esta migración reversa el asiento consolidado original (queda el REVERSO en el libro) y genera: (1) un asiento <code>purchase_invoice</code> por cada comprobante fiscal (base + IVA + percepciones, contra Anticipos a Rendir) + (2) un nuevo asiento <code>treasury_advance_settlement</code> reducido con solo los gastos no fiscales + diferencia de caja. Es idempotente: salta rendiciones ya migradas.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                    <div>Rendiciones a migrar: <b data-testid="adv-iva-mig-count">{advIvaMig.count}</b></div>
                    <div>IVA Crédito Fiscal afectado: <b>$ {Number(advIvaMig.iva_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</b></div>
                    <table className="w-full text-left mt-2">
                      <thead className="text-muted-foreground">
                        <tr><th>OP</th><th>Fecha</th><th>Beneficiario</th><th className="text-right">Comprobantes</th><th className="text-right">IVA</th></tr>
                      </thead>
                      <tbody>
                        {(advIvaMig.items || []).slice(0, 20).map((it) => (
                          <tr key={it.payment_order_id} className="border-t">
                            <td className="py-1 font-mono">{it.payment_order_number}</td>
                            <td className="py-1">{it.settlement_date || '-'}</td>
                            <td className="py-1">{it.recipient_name || '-'}</td>
                            <td className="py-1 text-right">{it.comprobante_count}</td>
                            <td className="py-1 text-right">$ {Number(it.iva_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {advIvaMig.count > 20 && <div className="text-muted-foreground">… y {advIvaMig.count - 20} más</div>}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={loadAdvIvaMig} data-testid="adv-iva-mig-recheck-btn">Volver a verificar</Button>
                    <Button onClick={applyAdvIvaMig} disabled={advIvaMigBusy} data-testid="adv-iva-mig-apply-btn">
                      {advIvaMigBusy ? 'Migrando...' : 'Aplicar migración'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* v3.42.3 · Reparar fechas de contra-asientos de la migración v3.42.0 */}
          <Card className={advIvaDates?.count > 0 ? 'border-amber-300' : ''} data-testid="adv-iva-dates-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Realinear fecha de contra-asientos de migración IVA (v3.42.3)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!advIvaDates ? (
                <p className="text-muted-foreground">Verificando…</p>
              ) : advIvaDates.count === 0 ? (
                <p className="text-muted-foreground" data-testid="adv-iva-dates-done">
                  ✔ Todos los contra-asientos de la migración v3.42.0 ya están fechados igual que su asiento original. La Posición de IVA mensual concilia sin falsos huérfanos.
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Las corridas de migración v3.42.0 anteriores a v3.42.2 generaban los contra-asientos con fecha del día del apply. Si el asiento original estaba en un mes anterior, la Posición de IVA de ese mes mostraba al original como huérfano positivo y el mes del apply mostraba al contra como huérfano negativo (netean en el anual pero ensucian la vista mensual). Este reparador reasigna la <code>date</code> del contra para que coincida con la del original. Es idempotente.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                    <div>Contra-asientos a realinear: <b data-testid="adv-iva-dates-count">{advIvaDates.count}</b></div>
                    <table className="w-full text-left mt-2">
                      <thead className="text-muted-foreground">
                        <tr><th>Contra #</th><th>Fecha contra</th><th>Original #</th><th>Fecha original</th></tr>
                      </thead>
                      <tbody>
                        {(advIvaDates.items || []).slice(0, 20).map((it, i) => (
                          <tr key={i} className="border-t">
                            <td className="py-1 font-mono">{it.reversal_entry_number}</td>
                            <td className="py-1 text-red-600">{it.reversal_date || '-'}</td>
                            <td className="py-1 font-mono">{it.original_entry_number}</td>
                            <td className="py-1 text-emerald-600">{it.original_date || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {advIvaDates.count > 20 && <div className="text-muted-foreground">… y {advIvaDates.count - 20} más</div>}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={loadAdvIvaDates} data-testid="adv-iva-dates-recheck-btn">Volver a verificar</Button>
                    <Button onClick={applyAdvIvaDates} disabled={advIvaDatesBusy} data-testid="adv-iva-dates-apply-btn">
                      {advIvaDatesBusy ? 'Reparando...' : 'Realinear fechas'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* v3.46.0 · Consistencia de stock (articles.stock == Σ lots) */}
          <Card
            className={stockConsistency?.action_needed
              ? 'border-amber-400 bg-amber-50/40'
              : stockConsistency !== null
                ? 'border-emerald-300'
                : ''}
            data-testid="stock-consistency-card"
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="w-4 h-4 text-muted-foreground" />
                  Consistencia de stock — contador vs. inventario de lotes (v3.46.0)
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadStockConsistency}
                  disabled={stockConsistencyChecking}
                  data-testid="stock-consistency-recheck-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${stockConsistencyChecking ? 'animate-spin' : ''}`} />
                  {stockConsistencyChecking ? 'Verificando…' : 'Verificar ahora'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {stockConsistency === null ? (
                <p className="text-muted-foreground">{stockConsistencyChecking ? 'Verificando consistencia…' : 'Presioná "Verificar ahora" para analizar.'}</p>
              ) : !stockConsistency.action_needed ? (
                <p className="text-emerald-700 font-medium" data-testid="stock-consistency-clean">
                  <CheckCircle2 className="inline w-4 h-4 mr-1 mb-0.5" />
                  El contador de stock de todos los artículos coincide exactamente con el inventario de lotes (FEFO). Sin acción necesaria.
                </p>
              ) : (
                <>
                  <p className="text-amber-700">
                    Se encontraron <b data-testid="stock-consistency-count">{stockConsistency.total_mismatches}</b> artículo(s) donde{' '}
                    <code className="bg-amber-100 px-1 rounded">articles.stock</code> difiere de{' '}
                    <code className="bg-amber-100 px-1 rounded">Σ(lotes activos)</code>.
                    Esto causa que la cifra de stock mostrada en el catálogo no coincida con el inventario real de lotes FEFO.
                    La reconciliación corrige el contador usando los lotes como fuente de verdad.
                    Se guarda un snapshot de respaldo antes de modificar.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-3 text-xs overflow-auto max-h-64">
                    <table className="w-full text-left" data-testid="stock-consistency-table">
                      <thead className="text-muted-foreground">
                        <tr>
                          <th className="py-1 pr-3">Artículo</th>
                          <th className="py-1 pr-3 text-right">Stock actual</th>
                          <th className="py-1 pr-3 text-right">Σ lotes</th>
                          <th className="py-1 pr-3 text-right">Δ</th>
                          <th className="py-1">Tipo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(stockConsistency.mismatches || []).slice(0, 60).map((m) => (
                          <tr key={m.article_id} className="border-t">
                            <td className="py-1 pr-3 font-medium">{m.name}</td>
                            <td className="py-1 pr-3 text-right font-mono">{m.stock_actual}</td>
                            <td className="py-1 pr-3 text-right font-mono text-emerald-700">{m.lots_sum}</td>
                            <td className={`py-1 pr-3 text-right font-mono font-semibold ${m.delta > 0 ? 'text-rose-600' : 'text-amber-600'}`}>
                              {m.delta > 0 ? `+${m.delta}` : m.delta}
                            </td>
                            <td className="py-1 text-muted-foreground">
                              {m.external_origin ? 'REM/OS' : 'Compras'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {stockConsistency.total_mismatches > 60 && (
                      <p className="text-center text-muted-foreground mt-1">… y {stockConsistency.total_mismatches - 60} más</p>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      onClick={applyStockConsistency}
                      disabled={stockConsistencyFixing}
                      data-testid="stock-consistency-apply-btn"
                    >
                      {stockConsistencyFixing
                        ? 'Reconciliando…'
                        : `Reconciliar ${stockConsistency.total_mismatches} artículo(s)`}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* v3.45.2 · Fix artículos con unit=null */}
          <Card className={unitNullFix?.action_needed ? 'border-red-400 bg-red-50/40' : 'border-emerald-300'} data-testid="unit-null-fix-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Fix artículos con unidad vacía — catálogo invisible (v3.45.2)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!unitNullFix ? (
                <p className="text-muted-foreground">Verificando…</p>
              ) : !unitNullFix.action_needed ? (
                <p className="text-muted-foreground" data-testid="unit-null-fix-done">
                  ✔ Sin artículos con unidad vacía. La base de datos está correcta — el catálogo no tiene riesgo de quedar invisible por este motivo.
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Se encontraron <b data-testid="unit-null-count">{unitNullFix.articles_with_null_unit}</b> artículo(s) con el campo <code>unit</code> vacío o nulo.
                    Esto causaba que <code>GET /api/articles</code> devolviera HTTP 500 y el catálogo apareciera completamente vacío (incluyendo los artículos recién homologados).
                    Esta herramienta los corrige seteando <code>unit = "unidad"</code>. Operación idempotente y sin riesgo.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-2">
                    <div>Artículos afectados: <b>{unitNullFix.articles_with_null_unit}</b></div>
                    <table className="w-full text-left mt-2">
                      <thead className="text-muted-foreground">
                        <tr>
                          <th className="py-1 pr-2">#</th>
                          <th className="py-1 pr-2">Nombre</th>
                          <th className="py-1 pr-2">Tipo</th>
                          <th className="py-1">Origen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(unitNullFix.articles || []).slice(0, 50).map((a, i) => (
                          <tr key={a.article_id} className="border-t">
                            <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                            <td className="py-1 pr-2 font-medium">
                              {a.name}
                              {a.pendiente_homologacion && <span className="ml-1 text-amber-600">[pendiente]</span>}
                            </td>
                            <td className="py-1 pr-2">{a.es_medicamento ? '💊 Med.' : 'Insumo'}</td>
                            <td className="py-1">{a.origen}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {unitNullFix.articles_with_null_unit > 50 && (
                      <div className="text-muted-foreground text-center">… y {unitNullFix.articles_with_null_unit - 50} más</div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={loadUnitNullFix} data-testid="unit-null-recheck-btn">Volver a verificar</Button>
                    <Button onClick={applyUnitNullFix} disabled={unitNullBusy} data-testid="unit-null-apply-btn">
                      {unitNullBusy ? 'Aplicando...' : `Corregir ${unitNullFix.articles_with_null_unit} artículo(s)`}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card className={ccZombies?.count > 0 ? 'border-amber-300' : ''} data-testid="cc-zombies-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Movimientos huérfanos en CC de Proveedores (v3.36.8)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!ccZombies ? (
                <p className="text-muted-foreground">Verificando…</p>
              ) : ccZombies.count === 0 ? (
                <p className="text-muted-foreground" data-testid="cc-zombies-done">
                  ✔ No hay movimientos huérfanos. Todas las Órdenes de Pago eliminadas tienen su rastro removido correctamente de las cuentas corrientes de proveedores.
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Detecta pagos imputados en <b>supplier_cc</b> cuya Orden de Pago original fue eliminada
                    pero el movimiento quedó zombie (fix v3.36.8: el borrado antiguo filtraba por texto libre
                    de la descripción; al desincronizarse del número real de la OP, el filtro no matcheaba).
                    Esta herramienta los lista y los elimina de forma segura — sólo marca como zombie los pagos
                    sin ninguna OP viva que respalde la cancelación de esa factura.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                    <div>Movimientos huérfanos: <b data-testid="cc-zombies-count">{ccZombies.count}</b></div>
                    <div>Monto total débitos huérfanos: <b>$ {Number(ccZombies.total_amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</b></div>
                    <table className="w-full text-left mt-2">
                      <thead className="text-muted-foreground">
                        <tr><th>Proveedor</th><th>OP real (eliminada)</th><th>FA</th><th className="text-right">Monto</th></tr>
                      </thead>
                      <tbody>
                        {(ccZombies.items || []).slice(0, 20).map((it) => (
                          <tr key={it.cc_id} className="border-t">
                            <td className="py-1">{it.supplier_name}</td>
                            <td className="py-1 font-mono">{it.deleted_op_number}</td>
                            <td className="py-1 font-mono">{it.ref_number}</td>
                            <td className="py-1 text-right">$ {Number(it.debit).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {ccZombies.count > 20 && <div className="text-muted-foreground">… y {ccZombies.count - 20} más</div>}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={loadCcZombies} data-testid="cc-zombies-recheck-btn">Volver a verificar</Button>
                    <Button onClick={applyCcZombies} disabled={ccZombiesBusy} data-testid="cc-zombies-apply-btn">
                      {ccZombiesBusy ? 'Limpiando...' : 'Limpiar movimientos huérfanos'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card className={links?.pending > 0 ? 'border-amber-300' : ''} data-testid="scheme-links-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Vinculación de convenios y pagadores (v3.11.0)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!links ? (
                <p className="text-muted-foreground">Verificando…</p>
              ) : links.pending === 0 ? (
                <p className="text-muted-foreground" data-testid="scheme-links-done">
                  ✔ No hay nada por vincular: todos los residentes tienen su convenio asignado y los
                  conceptos ya tienen definido quién los paga.
                  {links.residents_unmatched > 0 && ` (${links.residents_unmatched} residente(s) sin convenio y sin texto que coincida: hay que elegirlo a mano en la solapa Médico.)`}
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Normaliza el catálogo (clase de cada concepto, receptor de cada convenio y sus conceptos
                    vinculados) y completa el <b>convenio</b> de cada residente tomando el texto de obra social que quedó
                    cargado en la solapa Médico, y define <b>quién paga</b> cada concepto en los residentes que
                    hoy facturan a un familiar (para que nada cambie de comportamiento).
                    Se puede repetir: sólo completa lo que falta.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                    <div>Convenios a vincular: <b>{links.residents_linked}</b></div>
                    <div>Conceptos a asignar al familiar: <b>{links.payers_set}</b></div>
                    {links.residents_unmatched > 0 && (
                      <div className="text-amber-700">Sin coincidencia (elegir a mano): <b>{links.residents_unmatched}</b></div>
                    )}
                    {links.linked?.slice(0, 12).map((c, i) => (
                      <div key={i} className="font-mono">{c.full_name}: «{c.text}» → {c.insurer_name}</div>
                    ))}
                    {links.payers?.slice(0, 8).map((c, i) => (
                      <div key={`p${i}`} className="font-mono">{c.full_name} · {c.service_name} → paga {c.responsible_name}</div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={loadLinks} data-testid="scheme-links-recheck-btn">Volver a verificar</Button>
                    <Button onClick={applyLinks} disabled={linksBusy} data-testid="scheme-links-apply-btn">
                      {linksBusy ? 'Aplicando...' : 'Aplicar vinculación'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card className={act?.pending > 0 ? 'border-amber-300' : ''} data-testid="activate-prestacional-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Activar el concepto del convenio en toda la nómina</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!act ? (
                <p className="text-muted-foreground">Verificando…</p>
              ) : act.pending === 0 ? (
                <p className="text-muted-foreground" data-testid="activate-prestacional-done">
                  ✔ No hay nada por activar: los {act.already_active} concepto(s) prestacional(es) que
                  corresponden por convenio ya están encendidos en las fichas.
                  {act.no_convenio > 0 && ` (${act.no_convenio} residente(s) sin convenio asignado: hay que elegirlo en la solapa Médico.)`}
                  {act.convenio_sin_conceptos > 0 && ` (${act.convenio_sin_conceptos} residente(s) con un convenio que todavía no tiene conceptos tildados.)`}
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Recorre la nómina activa y enciende, en la solapa Facturación de cada ficha, el
                    concepto <b>prestacional</b> que le corresponde según su convenio (PAMI, IOMA, Particular…),
                    con el <b>precio del catálogo</b> y vigencia <b>desde hoy</b>. Los conceptos <b>adicionales</b>
                    {' '}(pañales, complementarios) no se tocan: se siguen cargando ficha por ficha.
                    Se puede repetir: saltea lo que ya está activo.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                    <div>Conceptos a activar: <b data-testid="activate-prestacional-pending">{act.pending}</b></div>
                    <div>Ya activos (no se tocan): <b>{act.already_active}</b></div>
                    {Object.entries(act.by_convenio || {}).map(([name, n]) => (
                      <div key={name}>· {name}: <b>{n}</b></div>
                    ))}
                    {act.no_convenio > 0 && (
                      <div className="text-amber-700">Sin convenio asignado (quedan afuera): <b>{act.no_convenio}</b></div>
                    )}
                    {act.convenio_sin_conceptos > 0 && (
                      <div className="text-amber-700">Convenio sin conceptos tildados: <b>{act.convenio_sin_conceptos}</b></div>
                    )}
                    {act.disabled_manually > 0 && (
                      <div className="text-amber-700">Apagados a mano en la ficha (no se tocan): <b>{act.disabled_manually}</b></div>
                    )}
                    {act.to_create?.slice(0, 12).map((c, i) => (
                      <div key={i} className="font-mono">{c.full_name} ({c.insurer_name}) → {c.service_name}</div>
                    ))}
                    {act.pending > 12 && <div className="text-muted-foreground">… y {act.pending - 12} más</div>}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={loadActivation} data-testid="activate-prestacional-recheck-btn">Volver a verificar</Button>
                    <Button onClick={applyActivation} disabled={actBusy} data-testid="activate-prestacional-apply-btn">
                      {actBusy ? 'Activando...' : 'Activar conceptos'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {user?.role === 'admin' && <LegacyInvoiceImportPanel />}
          {user?.role === 'admin' && <LegacyBulkImportPanel />}

          {/* v3.41.1 · Tarjeta "Normalizar adicionales 400/801" removida por pedido del usuario (fix ya aplicado en producción) */}
          {/* v3.41.1 · Tarjeta "Corregir descripciones en Libro Diario (Cobro Residente)" removida por pedido del usuario (fix ya aplicado en producción) */}


          {/* v3.28.25 · Migraciones históricas (ya ejecutadas) — agrupadas en un colapsable */}
          <details className="rounded-md border border-slate-200 bg-slate-50/50" data-testid="legacy-migrations-group">
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 flex items-center gap-2">
              <Construction className="w-4 h-4 text-slate-500" />
              Migraciones históricas (ya ejecutadas)
              <Badge variant="outline" className="ml-auto text-xs">2 disponibles</Badge>
            </summary>
            <div className="p-3 space-y-3">
              <p className="text-xs text-muted-foreground">
                Estas migraciones son <b>idempotentes</b> — muestran «✔ No hay nada por migrar» si ya se ejecutaron. Se conservan por si aparecen datos legacy o después de un restore de backup viejo.
              </p>

          <Card className={mig?.pending > 0 ? 'border-amber-300' : ''} data-testid="migration-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Migración v2.92.0 — Notas de Crédito</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!mig ? (
                <p className="text-muted-foreground">Verificando estado de la migración…</p>
              ) : mig.pending === 0 ? (
                <p className="text-muted-foreground" data-testid="migration-done">
                  ✔ No hay nada por migrar: las facturas ya tienen separado lo cobrado de lo acreditado
                  por Notas de Crédito y todas tienen su letra A/B asignada.
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Ajusta las facturas emitidas antes de esta versión: separa lo <b>acreditado por Notas
                    de Crédito</b> de lo <b>cobrado</b>, recalcula el estado y completa la letra A/B.
                    Lo de abajo es la verificación (no toca datos) y se puede repetir: no vuelve a mover lo ya migrado.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                    <div>Facturas a recalcular: <b>{mig.invoices_to_fix}</b></div>
                    <div>Letras a completar: <b>{mig.letters_to_backfill}</b></div>
                    {mig.changes?.map((c, i) => (
                      <div key={i} className="font-mono">
                        {c.invoice_number}: cobrado {c.paid_before} → {c.paid_after} · acreditado {c.credited_after} · {c.status_before} → {c.status_after}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={loadMigration} data-testid="migration-recheck-btn">
                      Volver a verificar
                    </Button>
                    <Button onClick={applyMigration} disabled={migBusy} data-testid="migration-apply-btn">
                      {migBusy ? 'Aplicando...' : 'Aplicar migración'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card className={revMig?.pending > 0 ? 'border-amber-300' : ''} data-testid="reversal-migration-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Migración v2.93.0 — Reversos del Libro Diario</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!revMig ? (
                <p className="text-muted-foreground">Verificando estado de la migración…</p>
              ) : revMig.pending === 0 ? (
                <p className="text-muted-foreground" data-testid="reversal-migration-done">
                  ✔ No hay nada por migrar: ningún asiento quedó oculto por el criterio anterior de borrado lógico.
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Antes de esta versión, eliminar un comprobante de compra o una orden de pago <b>escondía</b> su
                    asiento. Ahora se genera un <b>contra-asiento visible</b>. Esta migración lo aplica hacia atrás:
                    crea el reverso con la fecha en que se eliminó el documento y devuelve el asiento original al
                    Libro Diario marcado como reversado. Se puede repetir: no duplica lo ya reversado.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1" data-testid="reversal-migration-preview">
                    <div>Asientos a reversar: <b>{revMig.entries_to_reverse}</b></div>
                    {revMig.changes?.map((c, i) => (
                      <div key={i} className="font-mono">
                        #{c.entry_number} · {c.reference}{c.entity ? ` · ${c.entity}` : ''} · reverso con fecha {c.reversal_date}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={loadReversalMigration} data-testid="reversal-migration-recheck-btn">
                      Volver a verificar
                    </Button>
                    <Button onClick={applyReversalMigration} disabled={revMigBusy} data-testid="reversal-migration-apply-btn">
                      {revMigBusy ? 'Aplicando...' : 'Aplicar migración'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
            </div>
          </details>
          {checks.map((c) => {
            const result = checkResults[c.id];
            const isRunning = runningCheckId === c.id;
            return (
              <Card key={c.id} data-testid={`check-card-${c.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {c.label}
                        <Badge className={SEVERITY_STYLES[c.severity]}>{c.severity}</Badge>
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{c.description}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => runCheck(c.id)} disabled={isRunning} data-testid={`btn-check-${c.id}`}>
                        {isRunning ? '...' : 'Verificar'}
                      </Button>
                      <Button
                        size="sm"
                        variant={result?.count > 0 ? 'default' : 'outline'}
                        disabled={!result || result.count === 0}
                        onClick={() => setConfirmRepair(c)}
                        data-testid={`btn-repair-${c.id}`}
                      >
                        <Wrench className="w-3.5 h-3.5 mr-1" /> {c.repair_label}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {result && (
                  <CardContent className="pt-0">
                    <div className="rounded bg-slate-50 p-3 text-sm">
                      {result.count === 0 ? (
                        <div className="flex items-center gap-2 text-emerald-700">
                          <CheckCircle2 className="w-4 h-4" /> Sin hallazgos. Todo en orden.
                        </div>
                      ) : (
                        <>
                          <div className="font-semibold text-rose-700 mb-2">{result.preview_label}</div>
                          {result.samples && result.samples.length > 0 && (
                            <pre className="text-xs overflow-x-auto bg-white p-2 rounded border max-h-60 overflow-y-auto">
                              {JSON.stringify(result.samples, null, 2)}
                            </pre>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </TabsContent>

        {/* ====================== PROGRAMADAS ====================== */}
        <TabsContent value="scheduled" className="space-y-4">
          <Card className="bg-slate-50/40">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-cyan-600" /> Backup completo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p>Genera un Excel con todas las colecciones críticas (artículos, depósitos, lotes, movimientos, OPI, devoluciones, REM, áreas, proveedores, NP/OC/recepciones, Tesorería completa, usuarios, últimos 5.000 audit logs). Incluye hoja RESUMEN con estado por colección.</p>
              <Button onClick={onFullBackup} disabled={fullBackupExporting} data-testid="export-all-button">
                {fullBackupExporting ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : (
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                )}
                Descargar backup completo (Excel)
              </Button>
            </CardContent>
          </Card>

          {/* Backup Completo (ZIP) + Restauración */}
          <Card className="border-cyan-200" data-testid="backup-restore-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="w-4 h-4 text-cyan-600" /> Backup Completo (ZIP) y Restauración
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {backupStatus && backupStatus.is_stale && (
                <Alert variant="warning" className="border-amber-300 bg-amber-50" data-testid="backup-stale-alert">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <AlertTitle className="text-amber-900">Backup pendiente de hoy</AlertTitle>
                  <AlertDescription className="text-amber-800">
                    {backupStatus.last_at
                      ? `El último backup completo fue hace ${backupStatus.hours_ago}h (por ${backupStatus.last_by || '—'}). Te recomendamos generar uno nuevo.`
                      : 'Todavía no se ha realizado ningún backup completo. Te recomendamos hacerlo ahora.'}
                  </AlertDescription>
                </Alert>
              )}
              <div>
                <p className="mb-2">Descargá un <strong>.zip</strong> con TODA la base de datos (todas las colecciones). Es el archivo que se usa para <strong>restaurar</strong> el sistema.</p>
                <Button onClick={onZipBackup} disabled={zipBackupExporting} data-testid="full-backup-btn">
                  {zipBackupExporting ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Descargar Backup Completo (ZIP)
                </Button>
              </div>

              <div className="border-t border-border pt-4 space-y-3" data-testid="restore-backup-section">
                <div className="flex items-center gap-2 text-red-700 font-semibold">
                  <RotateCcw className="w-4 h-4" /> Restaurar Backup
                </div>
                <Alert variant="warning" className="border-red-300 bg-red-50">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <AlertTitle className="text-red-800">Acción destructiva e irreversible</AlertTitle>
                  <AlertDescription className="text-red-700 text-sm">
                    Restaurar un <strong>.zip de Backup Completo</strong> <strong>PISA por completo</strong> las colecciones incluidas (los datos actuales se pierden).
                    Se recomienda descargar primero un backup del estado actual. Tras restaurar, puede que tengas que volver a iniciar sesión.
                  </AlertDescription>
                </Alert>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="md:col-span-1">
                    <Label htmlFor="restore-file">Archivo de backup (.zip)</Label>
                    <Input
                      id="restore-file"
                      type="file"
                      accept=".zip"
                      data-testid="restore-file-input"
                      onChange={(e) => { setRestoreFile(e.target.files?.[0] || null); setRestoreResult(null); }}
                    />
                    {restoreFile && (
                      <p className="text-xs text-muted-foreground mt-1">{restoreFile.name} ({(restoreFile.size / 1024 / 1024).toFixed(2)} MB)</p>
                    )}
                  </div>
                  <div className="md:col-span-1">
                    <Label htmlFor="restore-confirm">Escribí <span className="font-mono font-bold">RESTAURAR</span> para confirmar</Label>
                    <Input
                      id="restore-confirm"
                      value={restoreConfirm}
                      onChange={(e) => setRestoreConfirm(e.target.value)}
                      placeholder="RESTAURAR"
                      data-testid="restore-confirm-input"
                    />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <Button
                      variant="destructive"
                      className="w-full"
                      data-testid="restore-backup-btn"
                      onClick={handleRestoreBackup}
                      disabled={restoring || !restoreFile || restoreConfirm.trim() !== 'RESTAURAR'}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {restoring ? 'Restaurando…' : 'Restaurar (pisa todos los datos)'}
                    </Button>
                  </div>
                </div>
                {restoreResult && (
                  <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm" data-testid="restore-result">
                    <p className="font-semibold text-green-800">✓ Restauración completada</p>
                    <p className="text-green-700">{restoreResult.collections} colecciones · {restoreResult.total_docs} documentos restaurados.</p>
                    {restoreResult.skipped?.length > 0 && (
                      <p className="text-green-700 mt-1">Omitidas (sesiones/cache): {restoreResult.skipped.join(', ')}</p>
                    )}
                    {restoreResult.dup_skipped && Object.keys(restoreResult.dup_skipped).length > 0 && (
                      <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-amber-900" data-testid="restore-dups">
                        <p className="font-semibold">⚠️ Duplicados descartados por índice único</p>
                        <p className="text-xs mb-1">El backup contenía registros con la misma clave — se descartó uno de cada par (se recomienda revisar en la base de origen).</p>
                        <ul className="list-disc ml-5 text-xs">
                          {Object.entries(restoreResult.dup_skipped).map(([coll, arr]) => (
                            <li key={coll}><strong>{coll}</strong>: {arr.length} registro(s) — <span className="font-mono">{arr.slice(0, 3).join(', ')}{arr.length > 3 ? ` … +${arr.length - 3} más` : ''}</span></li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Backup automático en Google Drive (off-site) */}
          <Card className="border-cyan-200" data-testid="drive-backup-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Cloud className="w-4 h-4 text-cyan-600" /> Backup automático en Google Drive
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                Vinculá una cuenta de Google para que el <strong>backup automático diario</strong> se copie a tu Google Drive (respaldo fuera del servidor).
                Se conservan las últimas <strong>{driveStatus?.max_backups || 14}</strong> copias en la carpeta <strong>{driveStatus?.folder_name || 'Hogar Gestion Backups'}</strong>.
              </p>
              {driveStatus?.connected ? (
                <div className="space-y-3" data-testid="drive-connected-panel">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-green-100 text-green-800 gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Conectado</Badge>
                    {driveStatus.connected_email && <span className="font-medium" data-testid="drive-connected-email">{driveStatus.connected_email}</span>}
                  </div>
                  <div className="grid gap-1 text-muted-foreground">
                    {driveStatus.connected_at && <div>Vinculado el: {fmtDateTime(driveStatus.connected_at)}</div>}
                    <div data-testid="drive-last-upload">Última copia subida: {driveStatus.last_upload_at ? `${fmtDateTime(driveStatus.last_upload_at)} (${driveStatus.last_upload_file || '—'})` : 'Todavía no se subió ninguna'}</div>
                  </div>
                  <div className="flex flex-wrap gap-3 pt-1">
                    <Button onClick={handleTestDriveBackup} disabled={driveBusy} data-testid="drive-test-backup-btn">
                      <CloudUpload className="w-4 h-4 mr-2" /> {driveBusy ? 'Subiendo…' : 'Probar subida ahora'}
                    </Button>
                    <Button variant="outline" onClick={handleDisconnectDrive} disabled={driveBusy} className="text-red-600 hover:text-red-700" data-testid="drive-disconnect-btn">
                      <Unplug className="w-4 h-4 mr-2" /> Desvincular
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3" data-testid="drive-disconnected-panel">
                  <Badge variant="secondary" className="gap-1"><Unplug className="w-3.5 h-3.5" /> No conectado</Badge>
                  <p className="text-muted-foreground">Al conectar, Google te pedirá permiso para que la app cree y administre <strong>únicamente sus propios archivos de backup</strong> en tu Drive (scope <code>drive.file</code>). No accede al resto de tus archivos.</p>
                  {driveStatus && !driveStatus.configured && (
                    <p className="text-red-600">⚠ El servidor no tiene configuradas las credenciales de Google Drive.</p>
                  )}
                  <Button onClick={handleConnectDrive} disabled={driveBusy || (driveStatus && !driveStatus.configured)} data-testid="drive-connect-btn">
                    <Cloud className="w-4 h-4 mr-2" /> {driveBusy ? 'Abriendo Google…' : 'Conectar Google Drive'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Cron jobs activos (hora Argentina)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Próxima corrida</TableHead>
                    <TableHead className="text-right">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scheduled.map((j) => (
                    <TableRow key={j.id} data-testid={`job-${j.id}`}>
                      <TableCell className="font-mono text-xs">{j.id}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{j.trigger}</TableCell>
                      <TableCell className="text-sm">{j.next_run && fmtDateTime(j.next_run)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => runJobNow(j.id)} disabled={runningJobId === j.id} data-testid={`run-job-${j.id}`}>
                          <PlayCircle className="w-3.5 h-3.5 mr-1" /> {runningJobId === j.id ? '...' : 'Ejecutar ahora'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Historial (últimas 50 corridas)</CardTitle></CardHeader>
            <CardContent>
              {jobsLog.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin corridas registradas todavía.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job</TableHead>
                      <TableHead>Cuándo</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Detalle</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobsLog.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{l.job}</TableCell>
                        <TableCell className="text-xs">{l.ran_at && fmtDateTime(l.ran_at)}</TableCell>
                        <TableCell>
                          {l.ok ? <Badge className="bg-emerald-100 text-emerald-800">OK</Badge> : <Badge className="bg-rose-100 text-rose-800">FAIL</Badge>}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {l.error ? <span className="text-rose-700">{l.error}</span> : JSON.stringify(l.details)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card className="bg-slate-50/40">
            <CardHeader><CardTitle className="text-base">Health-check público</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <p>Endpoint <code className="bg-white px-2 py-0.5 rounded border">GET /api/health</code> está disponible sin autenticación.</p>
              <p>Conectalo a un monitor externo (ej. UptimeRobot) para recibir mail si la app se cae. Cuando quieras activarlo, avisame y te paso instrucciones.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====================== SESIONES ACTIVAS ====================== */}
        <TabsContent value="sessions" className="space-y-4" data-testid="sessions-tab-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Sesiones activas</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Usuarios con sesión vigente. Podés forzar el cierre de cualquier sesión "zombie".</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchSessions} disabled={sessionsLoading} data-testid="refresh-sessions-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${sessionsLoading ? 'animate-spin' : ''}`} /> Refrescar
              </Button>
            </CardHeader>
            <CardContent>
              {sessionsLoading ? (
                <p className="text-center py-8 text-muted-foreground">Cargando…</p>
              ) : sessions.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground" data-testid="sessions-empty">No hay sesiones activas.</p>
              ) : (
                <Table data-testid="sessions-table">
                  <TableHeader><TableRow>
                    <TableHead>Usuario</TableHead><TableHead>Acceso</TableHead><TableHead>Dispositivo / IP</TableHead>
                    <TableHead>Inicio</TableHead><TableHead>Última actividad</TableHead><TableHead className="text-center">Acción</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {sessions.map((s) => (
                      <TableRow key={s.session_id} className={s.is_current ? 'bg-blue-50/60 dark:bg-blue-950/20' : ''}>
                        <TableCell>
                          <div className="font-medium">{s.user_name}{s.is_current && <Badge className="ml-2 bg-blue-500" data-testid="current-session-badge">Esta sesión</Badge>}</div>
                          <div className="text-xs text-muted-foreground">{s.user_email}</div>
                          <Badge variant="outline" className="mt-1 text-[10px]">{s.user_role}</Badge>
                        </TableCell>
                        <TableCell><Badge variant="outline">{s.auth_provider === 'google' ? 'Google' : s.auth_provider === 'local' ? 'Email' : s.auth_provider}</Badge></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-sm"><Monitor className="w-3.5 h-3.5 text-muted-foreground" />{deviceLabel(s.user_agent)}</div>
                          <div className="text-xs text-muted-foreground font-mono">{s.ip}</div>
                        </TableCell>
                        <TableCell className="text-sm">{s.created_at ? fmtDateTime(s.created_at) : '—'}</TableCell>
                        <TableCell className="text-sm">{s.last_seen_at ? fmtDateTime(s.last_seen_at) : <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-center">
                          <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700"
                            onClick={() => setConfirmRevoke(s)} data-testid={`revoke-session-${s.session_id}`}>
                            <LogOut className="w-4 h-4 mr-1" /> Cerrar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={!!confirmRevoke}
        onOpenChange={(o) => !o && setConfirmRevoke(null)}
        title={confirmRevoke?.is_current ? 'Cerrar TU sesión actual' : 'Cerrar sesión'}
        description={confirmRevoke && (
          <>
            Vas a cerrar la sesión de <strong>{confirmRevoke.user_name}</strong> ({confirmRevoke.user_email}).<br /><br />
            {confirmRevoke.is_current
              ? <span className="text-rose-600">⚠ Es TU sesión actual. Si la cerrás, vas a tener que volver a iniciar sesión.</span>
              : 'El usuario deberá volver a iniciar sesión la próxima vez que use la app.'}<br /><br />
            ¿Continuar?
          </>
        )}
        confirmText="Cerrar sesión"
        destructive
        loading={revoking}
        onConfirm={revokeSession}
        testId="confirm-revoke-session"
      />

      <ConfirmDialog
        open={!!confirmRepair}
        onOpenChange={(o) => !o && setConfirmRepair(null)}
        title="Confirmar reparación"
        description={confirmRepair && (
          <>
            Vas a aplicar <strong>{confirmRepair.repair_label}</strong>.<br /><br />
            Esta acción modifica datos. La verificación previa encontró <strong>{checkResults[confirmRepair.id]?.count}</strong> registros afectados.<br /><br />
            ¿Continuar?
          </>
        )}
        confirmText="Aplicar reparación"
        destructive={confirmRepair?.severity !== 'safe'}
        loading={repairing}
        onConfirm={runRepair}
        testId="confirm-repair"
      />
    </div>
  );
}

export default MaintenanceCenter;
